import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
    id: string;
    message: string;
    type: ToastType;
}

interface ToastContextType {
    showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const timeoutIdsRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const showToast = useCallback((message: string, type: ToastType = 'info') => {
        const id = Math.random().toString(36).substring(2, 9);
        setToasts((prev) => [...prev, { id, message, type }]);

        // Auto dismiss
        const timeoutId = setTimeout(() => {
            removeToast(id);
        }, 3000);
        timeoutIdsRef.current.push(timeoutId);
    }, [removeToast]);

    useEffect(() => {
        return () => {
            timeoutIdsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
            timeoutIdsRef.current = [];
        };
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] flex flex-col gap-2 w-full max-w-sm px-4 pointer-events-none">
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        className={`
              pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl border animate-in slide-in-from-top-2 fade-in
              ${toast.type === 'success' ? 'bg-brand-900/95 border-gold-500/50 text-white' : ''}
              ${toast.type === 'error' ? 'bg-red-950/95 border-red-500/50 text-white' : ''}
              ${toast.type === 'info' ? 'bg-brand-900/95 border-brand-700 text-white' : ''}
            `}
                    >
                        {toast.type === 'success' && <CheckCircle size={18} className="text-gold-500 shrink-0" />}
                        {toast.type === 'error' && <AlertCircle size={18} className="text-red-500 shrink-0" />}
                        {toast.type === 'info' && <Info size={18} className="text-brand-400 shrink-0" />}

                        <p className="text-sm font-medium flex-1">{toast.message}</p>

                        <button
                            onClick={() => removeToast(toast.id)}
                            className="text-white/50 hover:text-white transition-colors"
                        >
                            <X size={16} />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};
